/**
 * Google Identity Services — o script que devolve o idToken no navegador.
 *
 * O backend não confia no cliente: ele verifica a assinatura do token e o `aud` contra os
 * client IDs configurados. Então o papel deste módulo é só **carregar o script e entregar o
 * token**; nenhuma decisão de identidade acontece aqui.
 *
 * O script é de terceiro e não vem do bundle — por isso o carregamento é sob demanda, e não
 * uma `<script>` no index.html: quem nunca abre a tela de login não paga por ele, e o app
 * continua funcionando se o domínio do Google estiver bloqueado.
 */

import { intlTag, t } from '@/lib/i18n';

/**
 * O `hl` é o que traduz o botão que o **Google** desenha.
 *
 * O campo `locale` do `renderButton` não bastou: com a interface inteira em inglês, o botão
 * continuava dizendo "Continuar com o Google". O idioma é decidido quando o script carrega, não
 * quando o botão é renderizado — e como o script carrega uma vez por página e o idioma é congelado
 * no boot, montar a URL aqui basta e não exige recarregar nada.
 */
const SRC = `https://accounts.google.com/gsi/client?hl=${encodeURIComponent(intlTag)}`;

export const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ?? '';

/** Falso quando o build não tem client ID — a tela não deve oferecer o que não pode cumprir. */
export const googleConfigured = GOOGLE_CLIENT_ID.length > 0;

interface CredentialResponse {
  credential?: string;
}

interface GoogleIdApi {
  initialize(config: {
    client_id: string;
    callback: (response: CredentialResponse) => void;
    // Sem isto, o GIS pode abrir o One Tap por conta própria numa tela que não pediu.
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }): void;
  renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdApi } };
  }
}

let loading: Promise<GoogleIdApi> | null = null;
let initialized = false;
/**
 * O handler corrente. `initialize` só pode rodar uma vez por página — o próprio GIS avisa que
 * "only the last initialized instance will be used" —, mas o React monta o efeito duas vezes
 * em StrictMode e o componente pode remontar. Guardar o callback aqui separa as duas vidas:
 * a inicialização é da página, o handler é de quem está na tela agora.
 */
let handler: ((credential: string) => void) | null = null;

/** Inicializa uma vez e aponta o callback para o consumidor atual. */
export async function initGoogleIdentity(
  onCredential: (credential: string) => void,
): Promise<GoogleIdApi> {
  const api = await loadGoogleIdentity();
  handler = onCredential;

  if (!initialized) {
    api.initialize({
      client_id: GOOGLE_CLIENT_ID,
      // `auto_select` faria a sessão renascer sozinha depois do logout — o usuário sairia e
      // voltaria a entrar sem pedir. Sair precisa continuar significando sair.
      auto_select: false,
      cancel_on_tap_outside: true,
      callback: (response) => {
        if (response.credential) handler?.(response.credential);
      },
    });
    initialized = true;
  }

  return api;
}

/**
 * Carrega o script uma vez por página e devolve a API.
 *
 * A promessa é memoizada porque dois botões (entrar e criar conta) podem pedir ao mesmo
 * tempo, e duas tags `<script>` do GIS na mesma página produzem inicialização dupla.
 */
export function loadGoogleIdentity(): Promise<GoogleIdApi> {
  if (loading) return loading;

  loading = new Promise<GoogleIdApi>((resolve, reject) => {
    const existing = window.google?.accounts?.id;
    if (existing) {
      resolve(existing);
      return;
    }

    const script = document.createElement('script');
    script.src = SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const api = window.google?.accounts?.id;
      if (api) resolve(api);
      else reject(new Error('Google Identity Services carregou sem expor a API'));
    };
    script.onerror = () => {
      // Deixa a próxima tentativa recarregar: uma falha de rede não deve congelar o botão
      // para o resto da sessão.
      loading = null;
      reject(new Error(t('errors.googleLoad')));
    };
    document.head.appendChild(script);
  });

  return loading;
}
